import React from "react";
import { Link } from "react-router";

import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";

import { enrichmentStatusColumn } from "@/components/common/status-columns";
import { EditButton } from "@/components/refine-ui/buttons/edit";
import { ShowButton } from "@/components/refine-ui/buttons/show";
import { Badge } from "@/components/ui/badge";
import { makeTooltip } from "@/components/ui/tooltip";
import { parseUnixTimestamp } from "@/lib/dateUtils";
import { IPerformanceData } from "@/types/performance";

// Performance startDate is an ISO string from the explore endpoint (see
// screenings); parse string or unix-number defensively.
function formatDate(raw: string | number | undefined): string {
  if (raw === undefined || raw === null || raw === "") return "—";
  const d = typeof raw === "number" ? parseUnixTimestamp(raw) : dayjs(raw);
  return d.isValid() ? d.format("DD/MM/YYYY HH:mm") : "—";
}

export const usePerformanceColumns = () =>
  React.useMemo<ColumnDef<IPerformanceData>[]>(
    () => [
      { id: "id", accessorKey: "document.id", header: "ID" },
      {
        id: "play",
        header: "Play",
        minSize: 200,
        size: 260,
        accessorFn: (row) =>
          row.document.play?.title ?? row.document._playTitle ?? "—",
        enableSorting: false,
        enableColumnFilter: false,
        cell: function render({ row, getValue }: any) {
          return makeTooltip(
            <Link
              to={`/performances/show/${row.original.document.id}`}
              className="block truncate font-medium hover:underline"
            >
              {getValue()}
            </Link>,
          );
        },
      },
      {
        // `venue` is a nested object in the `performances` Typesense
        // collection — only its leaf paths are indexed for filtering, so
        // the column id (sent verbatim as the filter field) must be
        // "venue.name", not "venue" (matches screenings).
        id: "venue.name",
        header: "Venue",
        size: 200,
        accessorFn: (row) => row.document.venue?.name ?? "—",
        enableSorting: false,
        cell: function render({ getValue }: any) {
          return makeTooltip(<div className="truncate">{getValue()}</div>);
        },
        meta: { filterType: "text" },
      },
      {
        id: "startDate",
        header: "Starts",
        size: 150,
        accessorFn: (row) => row.document.startDate,
        // Sortable + date-filterable like screenings' own startDate column.
        enableSorting: true,
        cell: function render({ getValue }: any) {
          return makeTooltip(formatDate(getValue()));
        },
        meta: { filterType: "date" },
      },
      {
        id: "source",
        header: "Source",
        size: 180,
        enableSorting: false,
        enableColumnFilter: false,
        // A performance carries no `metadata.source`/`sourceId` (unlike a
        // play — see IPlay.metadata), only `metadata.tina.site`, the Tina
        // scrape's site slug. Renders an em-dash for a curator-created
        // performance.
        accessorFn: (row) => row.document.metadata?.tina?.site ?? null,
        cell: function render({ getValue }: any) {
          const value = getValue();
          if (!value)
            return <span className="text-muted-foreground/60">—</span>;
          return makeTooltip(<div className="truncate">{value}</div>);
        },
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "document.status",
        enableSorting: false,
        size: 120,
        cell: function render({ getValue }: any) {
          const status = getValue();
          return status ? <Badge variant="default">{status}</Badge> : null;
        },
        meta: {
          filterType: "select",
          options: [
            { label: "Draft", value: "Draft" },
            { label: "Complete", value: "Complete" },
            { label: "Cancelled", value: "Cancelled" },
          ],
        },
      },
      // Immediately after Status — the shared definition, same as
      // screenings. A performance is NOT embeddable, so there is no
      // embedding column.
      enrichmentStatusColumn<IPerformanceData>(),
      {
        id: "createdAt",
        header: "Created At",
        // `createdAt` is optional on IPerformance, matching plays: read it
        // as null rather than feeding `parseUnixTimestamp(undefined)`,
        // which is both a type error and an "Invalid Date" cell.
        accessorFn: (row) =>
          row.document.createdAt == null
            ? null
            : parseUnixTimestamp(row.document.createdAt),
        cell: function render({ getValue }: any) {
          const value = getValue();
          if (!value)
            return <span className="text-muted-foreground/60">—</span>;
          return makeTooltip(value.format("DD/MM/YYYY HH:mm"));
        },
        enableColumnFilter: true,
        meta: { filterType: "date" },
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enablePinning: true,
        size: 100,
        cell: function render({ row }) {
          return (
            <div className="flex items-center gap-1">
              <ShowButton
                size="sm"
                hideText
                recordItemId={row.original.document.id}
              />
              <EditButton
                size="sm"
                hideText={true}
                recordItemId={row.original.document.id}
              />
            </div>
          );
        },
      },
    ],
    [],
  );
